import { useSyncExternalStore } from 'react';
import AdmissionPage from './pages/AdmissionPage';
import AssignmentsPage from './pages/AssignmentsPage';
import AttendancePage from './pages/AttendancePage';
import CalendarPage from './pages/CalendarPage';
import DiscussPage from './pages/DiscussPage';
import ExamPage from './pages/ExamPage';
import ExpensesPage from './pages/ExpensesPage';
import FacultyPage from './pages/FacultyPage';
import FleetPage from './pages/FleetPage';
import HomePage from './pages/HomePage';
import HRPage from './pages/HRPage';
import InventoryPage from './pages/InventoryPage';
import InvoicePage from './pages/InvoicePage';
import LibraryPage from './pages/LibraryPage';
import NotFoundPage from './pages/NotFoundPage';
import ParentPage from './pages/ParentPage';
import PayrollPage from './pages/PayrollPage';
import PurchasePage from './pages/PurchasePage';
import StudentPage from './pages/StudentPage';
import TimetablePage from './pages/TimetablePage';
import TimeOffPage from './pages/TimeOffPage';
import ToDosPage from './pages/ToDosPage';

export const pageRoutes = {
  '/': HomePage,
  '/admission': AdmissionPage,
  '/assignments': AssignmentsPage,
  '/attendance': AttendancePage,
  '/calendar': CalendarPage,
  '/discuss': DiscussPage,
  '/exam': ExamPage,
  '/expenses': ExpensesPage,
  '/faculty': FacultyPage,
  '/fleet': FleetPage,
  '/hr': HRPage,
  '/inventory': InventoryPage,
  '/invoice': InvoicePage,
  '/library': LibraryPage,
  '/parent': ParentPage,
  '/payroll': PayrollPage,
  '/purchase': PurchasePage,
  '/student': StudentPage,
  '/timetable': TimetablePage,
  '/time-off': TimeOffPage,
  '/to-dos': ToDosPage,
};

export { NotFoundPage };

function subscribe(callback) {
  window.addEventListener('popstate', callback);
  window.addEventListener('hashchange', callback);
  return () => {
    window.removeEventListener('popstate', callback);
    window.removeEventListener('hashchange', callback);
  };
}

function getPathname() {
  return window.location.pathname || '/';
}

function getLocation() {
  return `${window.location.pathname || '/'}${window.location.search || ''}${window.location.hash || ''}`;
}

export function usePathname() {
  return useSyncExternalStore(subscribe, getPathname, () => '/');
}

export function useLocationInfo() {
  const currentLocation = useSyncExternalStore(subscribe, getLocation, () => '/');
  const url = new URL(currentLocation, 'http://localhost');

  return {
    pathname: url.pathname || '/',
    search: url.search || '',
    hash: url.hash || '',
  };
}

function scrollToHash(hash) {
  const targetId = decodeURIComponent(String(hash || '').replace(/^#/, ''));

  if (!targetId) {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    return;
  }

  let attempts = 0;

  function tryScroll() {
    const target = document.getElementById(targetId);

    if (target) {
      const stickyHeader = document.querySelector('.portal-topbar');
      const headerOffset = stickyHeader
        ? stickyHeader.getBoundingClientRect().height + 18
        : 90;
      const targetTop = window.scrollY + target.getBoundingClientRect().top - headerOffset;

      window.scrollTo({
        top: Math.max(targetTop, 0),
        left: 0,
        behavior: 'auto',
      });
      return;
    }

    if (attempts < 20) {
      attempts += 1;
      window.requestAnimationFrame(tryScroll);
    }
  }

  tryScroll();
}

export function navigate(to) {
  const targetUrl = new URL(to, window.location.origin);
  const currentUrl = new URL(window.location.href);
  const nextLocation = `${targetUrl.pathname || '/'}${targetUrl.search || ''}${targetUrl.hash || ''}`;
  const currentLocation = `${currentUrl.pathname || '/'}${currentUrl.search || ''}${currentUrl.hash || ''}`;

  if (nextLocation === currentLocation) {
    if (targetUrl.hash) {
      scrollToHash(targetUrl.hash);
    } else {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    }
    return;
  }

  window.history.pushState({}, '', nextLocation);
  window.dispatchEvent(new PopStateEvent('popstate'));

  if (targetUrl.hash) {
    scrollToHash(targetUrl.hash);
  } else {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }
}

function hasSpecialKey(event) {
  return event.metaKey || event.altKey || event.ctrlKey || event.shiftKey;
}

export function RouteLink({ to, onClick, children, target, ...props }) {
  const isExternalLink = /^https?:\/\//.test(to);

  function handleClick(event) {
    onClick?.(event);

    if (
      event.defaultPrevented ||
      isExternalLink ||
      target === '_blank' ||
      hasSpecialKey(event)
    ) {
      return;
    }

    event.preventDefault();
    event.currentTarget.blur();
    navigate(to);
  }

  return (
    <a {...props} href={to} target={target} onClick={handleClick}>
      {children}
    </a>
  );
}
